import * as React from 'react'

import { difference, uniq, xor } from 'lodash'

import { observer } from 'mobx-react'

export interface WithMultiSelectStateChildrenProps<T> {
  items: T[]
  selected: T[]
  onToggleSelectedItem: (item: T) => void
  add: (items: T[]) => void
  remove: (items: T[]) => void
  onToggleRange: (item: T) => void
  onSelectAll: () => void
  onDeselectAll: () => void
}
interface Props<T> {
  items: T[]
  initialSelection?: T[]
}
interface State<T> {
  selected: T[]
  lastClickedItem: any
}

class WithMultiSelectState<T> extends React.Component<
  Props<T> & React.ConsumerProps<WithMultiSelectStateChildrenProps<T>>,
  State<T>
> {
  constructor(props: Props<T> & React.ConsumerProps<WithMultiSelectStateChildrenProps<T>>) {
    super(props)
    this.state = {
      selected: props.initialSelection || [],
      lastClickedItem: null,
    }
  }

  onSelectAll = () => {
    this.setState({
      selected: [...this.props.items],
    })
  }

  onDeselectAll = () => {
    this.setState({
      selected: [],
    })
  }

  add = (items: T[]) => {
    this.setState({
      selected: uniq(this.state.selected.concat(items)),
      lastClickedItem: items[items.length - 1],
    })
  }

  remove = (items: T[]) => {
    this.setState({
      selected: difference(this.state.selected, items),
      lastClickedItem: items[items.length - 1],
    })
  }

  onToggleRange = (item: T) => {
    const { lastClickedItem, selected } = this.state

    if (!lastClickedItem) return

    const { items } = this.props
    const lastClickedIndex = items.indexOf(lastClickedItem)
    const currentClickedIndex = items.indexOf(item)

    const [firstIndex, lastIndex] = [lastClickedIndex, currentClickedIndex].sort()

    const sliced = items.slice(firstIndex, lastIndex)
    this.setState({
      selected: uniq([...selected, ...sliced, item]),
      lastClickedItem: item,
    })
  }

  onToggleSelectedItem = (item: T) => {
    const { selected } = this.state

    this.setState({
      selected: xor(selected, [item]),
      lastClickedItem: item,
    })
  }

  render() {
    const { children, items } = this.props
    const { selected } = this.state

    return children({
      items,
      selected,
      add: this.add,
      remove: this.remove,
      onToggleSelectedItem: this.onToggleSelectedItem,
      onToggleRange: this.onToggleRange,
      onSelectAll: this.onSelectAll,
      onDeselectAll: this.onDeselectAll,
    })
  }
}

export default observer(WithMultiSelectState)
